import React from "react";
import { Check } from "lucide-react";

// 18-step Parts Request workflow.
// Display as a horizontally scrollable progression bar.
// Steps 1-15 represent the normal flow; 16 Closed, 17 Reopened, 18 Cancelled are terminal/exception states.
const STEPS = [
  "Pedido criado",
  "Aguarda validação",
  "Aguarda encomenda Alidata",
  "Aguarda encomenda à fábrica",
  "Encomendado",
  "Em trânsito",
  "Em despacho aduaneiro",
  "Recebido parcial",
  "Recebido completo",
  "Peças disponíveis",
  "Cliente por contactar",
  "Cliente contactado",
  "Entrada agendada",
  "Marcação falhada",
  "Entregue à oficina",
  "Encerrado",
  "Reaberto",
  "Cancelado",
];

const TERMINAL = new Set(["Encerrado", "Cancelado"]);

function classifyStep(stepName, currentIdx, idx, currentStatus) {
  if (stepName === currentStatus) return "current";
  if (idx < currentIdx && idx <= 14) return "done"; // main flow done
  if (TERMINAL.has(stepName) && stepName !== currentStatus) return "future";
  if (stepName === "Marcação falhada" && currentStatus !== "Marcação falhada") return "future";
  if (stepName === "Reaberto" && currentStatus !== "Reaberto") return "future";
  if (idx < currentIdx) return "done";
  return "future";
}

export default function PartsRequestProgressBar({ status }) {
  const currentIdx = STEPS.indexOf(status);
  const isTerminal = TERMINAL.has(status);

  return (
    <div className="bg-white border border-gray-200 rounded-md p-3 sm:p-4" data-testid="pr-progress-bar">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Workflow progress</div>
          <div className="text-sm font-semibold text-[#1A1A2E]">
            Step {currentIdx >= 0 ? currentIdx + 1 : "—"} of 18:{" "}
            <span className="font-mono text-[#1F3864]" data-testid="pr-progress-current">{status}</span>
          </div>
        </div>
        {isTerminal && (
          <span className="text-[10px] uppercase tracking-widest bg-[#E2E8F0] text-[#495057] px-2 py-0.5 rounded-sm font-semibold">
            Terminal state
          </span>
        )}
      </div>

      <div className="overflow-x-auto -mx-1 px-1 pb-1">
        <ol className="flex items-stretch min-w-full" data-testid="pr-progress-steps">
          {STEPS.map((step, idx) => {
            const cls = classifyStep(step, currentIdx, idx, status);
            return (
              <li key={step} className="flex items-stretch shrink-0" style={{ minWidth: "auto" }}>
                <Step name={step} index={idx} state={cls} isLast={idx === STEPS.length - 1} />
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

function Step({ name, index, state, isLast }) {
  // Colors per state
  const config = {
    done: {
      circle: "bg-[#28A745] border-[#28A745] text-white",
      line: "bg-[#28A745]",
      label: "text-[#155724]",
      sub: "text-[#6C757D]",
    },
    current: {
      circle: "bg-[#1F3864] border-[#1F3864] text-white ring-2 ring-[#1F3864]/30",
      line: "bg-gray-300",
      label: "text-[#1F3864] font-semibold",
      sub: "text-[#1F3864]/80",
    },
    future: {
      circle: "bg-white border-gray-300 text-[#94A3B8]",
      line: "bg-gray-200",
      label: "text-[#6C757D]",
      sub: "text-[#94A3B8]",
    },
  }[state];

  return (
    <div className="flex flex-col items-center px-2" style={{ minWidth: "92px" }} data-testid={`pr-progress-step-${index + 1}`}>
      <div className="flex items-center w-full">
        <div className="flex-1 h-0.5" style={{ background: index === 0 ? "transparent" : undefined }}>
          {index !== 0 && <div className={`h-0.5 ${state === "done" || state === "current" ? "bg-[#28A745]" : "bg-gray-200"}`} />}
        </div>
        <div
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-[10px] font-mono font-bold ${config.circle}`}
        >
          {state === "done" ? <Check className="w-3 h-3" strokeWidth={3} /> : index + 1}
        </div>
        <div className="flex-1 h-0.5">
          {!isLast && <div className={`h-0.5 ${state === "done" ? "bg-[#28A745]" : "bg-gray-200"}`} />}
        </div>
      </div>
      <div className={`text-[10px] mt-1.5 text-center leading-tight ${config.label}`} style={{ width: "92px" }}>
        {name}
      </div>
    </div>
  );
}
